import { memo } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import arrowBackFill from "@iconify/icons-eva/arrow-back-fill";
import eyeFill from "@iconify/icons-eva/eye-fill";
import menuFill from "@iconify/icons-eva/menu-fill";
import flashFill from "@iconify/icons-eva/flash-fill";
import PropTypes from "prop-types";
import { BRAND } from "src/theme/brandTokens";

const TopBarEdicion = memo(function TopBarEdicion({
  id,
  dirty,
  isSubmitting,
  isValid,
  onCancelar,
  onGuardar,
  onAbrirPreview,
  onAbrirNav,
  showPreviewButton,
  showNavButton,
  generadoConIA,
  onAbrirRegenerar,
}) {
  return (
    <Box
      sx={{
        // El contenedor padre tiene altura fija y el scroll vive dentro de la
        // columna central, por lo que esta barra queda naturalmente pinned como
        // primer flex child (patrón "freeze panes" — no requiere position: sticky).
        flexShrink: 0,
        bgcolor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
        px: { xs: 1.5, sm: 3 },
        py: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={{ xs: 0.5, sm: 1.5 }}
        sx={{ minWidth: 0, overflow: "hidden" }}
      >
        {showNavButton && (
          <IconButton size="small" onClick={onAbrirNav}>
            <Icon icon={menuFill} width={22} />
          </IconButton>
        )}
        <IconButton size="small" onClick={onCancelar}>
          <Icon icon={arrowBackFill} width={22} />
        </IconButton>
        {/* Título + Chip se ocultan en xs para dar aire a los botones de acción. */}
        <Typography
          variant="subtitle1"
          noWrap
          sx={{ fontWeight: 600, display: { xs: "none", sm: "block" } }}
        >
          Editar propuesta #{id}
        </Typography>
        <Chip
          label="BORRADOR"
          size="small"
          sx={{ display: { xs: "none", sm: "inline-flex" } }}
        />
        {generadoConIA && (
          <Chip
            size="small"
            icon={<Icon icon={flashFill} width={14} />}
            label="Generado con IA · ver prompt"
            onClick={onAbrirRegenerar}
            sx={{
              display: { xs: "none", md: "inline-flex" },
              bgcolor: BRAND.color.primaryBg,
              color: BRAND.color.primary,
              fontWeight: 600,
              fontSize: 11,
              border: `1px solid ${BRAND.color.primaryHoverBg}`,
              cursor: "pointer",
              "&:hover": { bgcolor: BRAND.color.primaryHoverBg },
            }}
          />
        )}
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        spacing={{ xs: 0.5, sm: 1 }}
        sx={{ flexShrink: 0 }}
      >
        {showPreviewButton && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Icon icon={eyeFill} width={16} />}
            onClick={onAbrirPreview}
            sx={{
              borderColor: "divider",
              color: "text.secondary",
              minWidth: { xs: 0, sm: "auto" },
              px: { xs: 1, sm: 1.5 },
              "& .MuiButton-startIcon": {
                mr: { xs: 0, sm: 1 },
              },
            }}
          >
            {/* En xs solo se ve el ícono */}
            <Box
              component="span"
              sx={{ display: { xs: "none", sm: "inline" } }}
            >
              Vista previa
            </Box>
          </Button>
        )}
        <Button
          variant="outlined"
          size="small"
          color="inherit"
          onClick={onCancelar}
          sx={{ px: { xs: 1, sm: 1.5 } }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={!dirty || isSubmitting}
          onClick={onGuardar}
          startIcon={
            isSubmitting ? <CircularProgress size={14} color="inherit" /> : null
          }
          sx={{
            bgcolor: BRAND.color.primary,
            "&:hover": { bgcolor: BRAND.color.primaryDark },
            position: "relative",
            px: { xs: 1, sm: 1.5 },
          }}
          title={
            dirty && !isValid
              ? "Hay campos incompletos — se guardarán igualmente"
              : undefined
          }
        >
          {isSubmitting ? "Guardando" : "Guardar"}
          {/* "cambios" solo en sm+ para ahorrar espacio en mobile */}
          <Box
            component="span"
            sx={{ display: { xs: "none", sm: "inline" }, ml: 0.5 }}
          >
            {isSubmitting ? "..." : "cambios"}
          </Box>
          {dirty && !isSubmitting && (
            <Box
              component="span"
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "#fff",
              }}
            />
          )}
        </Button>
      </Stack>
    </Box>
  );
});

TopBarEdicion.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  dirty: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isValid: PropTypes.bool,
  onCancelar: PropTypes.func.isRequired,
  onGuardar: PropTypes.func.isRequired,
  onAbrirPreview: PropTypes.func,
  onAbrirNav: PropTypes.func,
  showPreviewButton: PropTypes.bool,
  showNavButton: PropTypes.bool,
  generadoConIA: PropTypes.bool,
  onAbrirRegenerar: PropTypes.func,
};

TopBarEdicion.defaultProps = {
  isValid: true,
  onAbrirPreview: undefined,
  onAbrirNav: undefined,
  showPreviewButton: false,
  showNavButton: false,
  generadoConIA: false,
  onAbrirRegenerar: undefined,
};

export default TopBarEdicion;
